import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { MyFeatureAccessResponse } from '../models/feature-access.model';
import { FeatureAccessApiService } from './feature-access-api.service';

// Client-side fallback for a non-401 /me error. `failClosed: true` is the
// load-bearing discriminator (FEUC-FA-3 §C): it makes `canSee` hide EVERY key
// under a degraded model — an empty `previewKeys` alone would fall through to the
// happy-path `return true` and leak gated surfaces to a non-super-admin.
const FAIL_CLOSED: MyFeatureAccessResponse = {
  isSuperAdmin: false,
  previewKeys: [],
  enabledPreviewFeatures: [],
  failClosed: true,
};

function needsInteractiveB2cLogin(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  return (
    err instanceof BrowserAuthError &&
    err.errorCode === BrowserAuthErrorCodes.blockIframeReload
  );
}

@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private readonly api = inject(FeatureAccessApiService);

  readonly me = signal<MyFeatureAccessResponse | null>(null);
  readonly isLoading = signal(false);

  async load(): Promise<void> {
    this.isLoading.set(true);
    try {
      const resp = await firstValueFrom(this.api.getMe());
      this.me.set(resp);
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        // authInterceptor handles redirect; leave me() unchanged (null on cold boot).
        return;
      }
      if (needsInteractiveB2cLogin(err)) {
        // bearerToken() already invoked acquireTokenRedirect; the page is on its
        // way to Azure AD login. Leaving me() null keeps the app in cold-boot
        // state rather than settling into FAIL_CLOSED while the redirect commits.
        return;
      }
      console.error('FeatureAccessService.load failed; falling back to fail-closed defaults', err);
      this.me.set(FAIL_CLOSED);
    } finally {
      this.isLoading.set(false);
    }
  }

  reset(): void {
    this.me.set(null);
  }

  canSee(key: string): boolean {
    const m = this.me();
    if (!m) return false; // cold boot (me() === null) → hidden, no flash of preview UI
    if (m.failClosed) return false; // FAIL_CLOSED fallback → hide EVERY key (discriminator)
    if (m.isSuperAdmin) return true;
    if (m.previewKeys.includes(key)) return m.enabledPreviewFeatures.includes(key);
    return true; // unknown / non-preview key ⇒ visible — happy path ONLY
  }

  isSuperAdmin(): boolean {
    return this.me()?.isSuperAdmin === true;
  }
}
